import React, { useState } from 'react';
import { Save, Sparkles, UserPlus, Wand2, LayoutGrid } from 'lucide-react';
import { DateTime } from 'luxon';

/**
 * SRI LANKAN (SOUTH INDIAN) FIXED SIGN INDEXES:
 * 0: Pisces (Meena) - Top Left
 * 1: Aries (Mesha)
 * 2: Taurus (Vrishabha)
 * 3: Gemini (Mithuna) - Top Right
 * 4: Cancer (Kataka)
 * 5: Leo (Simha)
 * 6: Virgo (Kanya) - Bottom Right
 * 7: Libra (Thula)
 * 8: Scorpio (Vrishchika)
 * 9: Sagittarius (Dhanu) - Bottom Left
 * 10: Capricorn (Makara)
 * 11: Aquarius (Kumbha)
 */

const SINHALA_PLANETS = {
    Sun: "රවි", Moon: "සඳු", Mars: "කුජ", Mercury: "බුධ",
    Jupiter: "ගුරු", Venus: "සිකු", Saturn: "ශනි", Rahu: "රාහු", Ketu: "කේතු", Lagna: "ලග්"
};

// Maps 0-360 degrees to the correct fixed box index (starting from Aries at 0 deg)
// Note: We adjust by +1 because in our grid index, Pisces is 0.
// Aries starts at 0° longitude.
const getBoxIndexFromDegree = (degree) => {
    const ariesIndex = Math.floor(degree / 30); // 0=Aries, 1=Taurus... 11=Pisces
    const southIndianGridIndex = (ariesIndex + 1) % 12; // Shifts so Pisces is 0
    return southIndianGridIndex;
};

export default function NewPatient() {
    const [isChartGenerated, setIsChartGenerated] = useState(false);
    const [loading, setLoading] = useState(false);

    // Form States
    const [birthDate, setBirthDate] = useState("");
    const [birthTime, setBirthTime] = useState("");

    const [lagnaData, setLagnaData] = useState(Array(12).fill([]).map(() => []));
    const [thathkalaData, setThathkalaData] = useState(Array(12).fill([]).map(() => []));

    const calculateVedicChart = (date, time) => {
        const combinedDateTime = DateTime.fromISO(`${date}T${time}`);
        const seed = combinedDateTime.toMillis();

        // Simulated Longitudes (0-360) based on seed
        const getLong = (offset) => (Math.abs(Math.sin(seed * offset)) * 360) % 360;

        const planetPositions = [
            { name: SINHALA_PLANETS.Lagna, deg: getLong(0.0001) },
            { name: SINHALA_PLANETS.Sun, deg: getLong(0.0002) },
            { name: SINHALA_PLANETS.Moon, deg: getLong(0.0003) },
            { name: SINHALA_PLANETS.Mars, deg: getLong(0.0004) },
            { name: SINHALA_PLANETS.Mercury, deg: getLong(0.0005) },
            { name: SINHALA_PLANETS.Jupiter, deg: getLong(0.0006) },
            { name: SINHALA_PLANETS.Venus, deg: getLong(0.0007) },
            { name: SINHALA_PLANETS.Saturn, deg: getLong(0.0008) },
            { name: SINHALA_PLANETS.Rahu, deg: getLong(0.0009) },
            { name: SINHALA_PLANETS.Ketu, deg: (getLong(0.0009) + 180) % 360 },
        ];

        const grid = Array(12).fill(null).map(() => []);

        planetPositions.forEach(p => {
            const boxIdx = getBoxIndexFromDegree(p.deg);
            grid[boxIdx].push(p.name);
        });

        return grid;
    };

    const handleGenerateChart = () => {
        if (!birthDate || !birthTime) {
            alert("කරුණාකර උපන් දිනය සහ වේලාව ඇතුළත් කරන්න.");
            return;
        }
        setLoading(true);
        setTimeout(() => {
            const result = calculateVedicChart(birthDate, birthTime);
            setLagnaData(result);
            setThathkalaData(result);
            setIsChartGenerated(true);
            setLoading(false);
        }, 800);
    };

    return (
        <div className="p-4 md:p-8 bg-slate-50 min-h-screen font-sinhala">
            <header className="mb-8 border-b border-slate-200 pb-6">
                <h1 className="text-3xl font-bold text-slate-800 tracking-tight text-center lg:text-left">අලුත් රෝගියෙකු ඇතුළත් කිරීම</h1>
            </header>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                {/* Information Inputs */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800 mb-6 border-b pb-4">
                            <UserPlus className="text-emerald-500" size={20}/> මූලික තොරතුරු
                        </h2>
                        <div className="space-y-4">
                            <InputField label="ජාතික හැඳුනුම්පත් අංකය" />
                            <div className="grid grid-cols-2 gap-4">
                                <InputField label="උපන් දිනය" type="date" onChange={(e) => setBirthDate(e.target.value)} />
                                <InputField label="උපන් වේලාව" type="time" onChange={(e) => setBirthTime(e.target.value)} />
                            </div>
                            <InputField label="ලිපිනය" />
                            <InputField label="දුරකථන අංකය" />
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                        <TextAreaField label="රෝග විස්තරය / ඉතිහාසය" />
                    </div>
                </div>

                {/* Charts Display */}
                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm h-fit">
                    <div className="flex justify-between items-center border-b pb-4 mb-6">
                        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800">
                            <Sparkles className="text-indigo-500" size={20}/> ජ්‍යෝතිෂ්‍ය තොරතුරු
                        </h2>
                        <button
                            onClick={handleGenerateChart}
                            disabled={loading}
                            className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-2xl text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-lg shadow-indigo-100"
                        >
                            <Wand2 size={16} className={loading ? "animate-spin" : ""}/>
                            {loading ? "ගණනය කරමින්..." : "කේන්ද්‍රය ජනනය කරන්න"}
                        </button>
                    </div>

                    {!isChartGenerated ? (
                        <div className="h-64 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 gap-3">
                            <LayoutGrid size={40} className="opacity-20" />
                            <p className="text-sm italic">දිනය සහ වේලාව ඇතුළත් කර කේන්ද්‍රය ජනනය කරන්න</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-900 p-8 rounded-[2rem] shadow-inner">
                            <AstrologyChart title="ලග්න කේන්ද්‍රය" data={lagnaData} />
                            <AstrologyChart title="තත්කාල කේන්ද්‍රය" data={thathkalaData} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const AstrologyChart = ({ title, data }) => {
    // Fixed South Indian Clockwise Layout
    // 0=Meena, 1=Mesha, 2=Vrishabha, 3=Mithuna, 4=Kataka, 5=Simha, 6=Kanya, 7=Thula, 8=Vrishchika, 9=Dhanu, 10=Makara, 11=Kumbha
    const visualLayout = [
        0,  1,  2,  3,
        11, 'm', 'm', 4,
        10, 'm', 'm', 5,
        9,  8,  7,  6
    ];

    return (
        <div className="flex flex-col items-center">
            <h3 className="text-slate-400 text-[10px] font-bold uppercase mb-4 tracking-widest">{title}</h3>
            <div className="grid grid-cols-4 w-full aspect-square border-2 border-slate-700 bg-slate-800/50">
                {visualLayout.map((signIdx, i) => {
                    if (signIdx === 'm') return <div key={i} className="border-[0.5px] border-slate-700 bg-slate-900/40"></div>;

                    return (
                        <div key={i} className="border-[0.5px] border-slate-700 flex flex-col items-center justify-center p-1 min-h-[50px]">
                            {data[signIdx]?.map((p, pi) => (
                                <span key={pi} className={`text-[10px] font-bold leading-tight ${p === 'ලග්' ? 'text-yellow-400' : 'text-slate-100'}`}>
                                    {p}
                                </span>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* --- UI COMPONENTS --- */
const InputField = ({ label, type = "text", onChange }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[11px] font-bold text-slate-500 ml-1 uppercase">{label}</label>
        <input type={type} onChange={onChange} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-indigo-500 transition-all" />
    </div>
);

const TextAreaField = ({ label }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[11px] font-bold text-slate-500 ml-1 uppercase">{label}</label>
        <textarea rows="4" className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-indigo-500 transition-all resize-none" />
    </div>
);